import React, { useEffect, useState } from "react";

const CUISINES = [
  "American",
  "Chinese",
  "French",
  "Greek",
  "Indian",
  "Italian",
  "Japanese",
  "Korean",
  "Mediterranean",
  "Middle Eastern",
  "Mexican",
  "Spanish",
  "Thai",
  "Vietnamese",
  "Other",
];

const COURSES = [
  "Appetizer",
  "Beverage",
  "Bread",
  "Dessert",
  "Main Entrée",
  "Salad",
  "Sauce/Condiment",
  "Side Dish",
  "Snack",
  "Soup",
  "Other",
];

const SORT_OPTIONS = [
  { value: "", label: "-- Sort Recipes --" },
  { value: "cookTime-asc", label: "Cook Time, Low to High" },
  { value: "created-desc", label: "Most Recent" },
  { value: "ingredients-asc", label: "Ingredients, Low to High" },
];

const Filters = ({ filters, onChange, user }) => {
  const [localFilters, setLocalFilters] = useState(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const updateFilters = (updater) => {
    setLocalFilters(updater);
    onChange(updater);
  };

  const handleSelectChange = ({ target: { name, value } }) => {
    const nextFilters = { ...localFilters };

    if (value) {
      nextFilters[name] = value;
    } else {
      delete nextFilters[name];
    }

    updateFilters(nextFilters);
  };

  const handleCheckboxChange = ({ target: { name, checked } }) => {
    if (!user) {
      return;
    }

    const nextFilters = { ...localFilters };

    if (checked) {
      nextFilters[name] = user.id;
    } else {
      delete nextFilters[name];
    }

    updateFilters(nextFilters);
  };

  const handleTextChange = ({ target: { name, value } }) => {
    const nextFilters = { ...localFilters };

    if (value.trim()) {
      nextFilters[name] = value;
    } else {
      delete nextFilters[name];
    }

    updateFilters(nextFilters);
  };

  const renderSelect = (name, value, options) => (
    <div className="relative">
      <select
        name={name}
        value={value ?? ""}
        onChange={handleSelectChange}
        className="w-full cursor-pointer appearance-none rounded border border-slate-200 bg-transparent py-2 pl-3 pr-8 text-sm text-slate-700 transition duration-300 ease focus:border-slate-400 focus:outline-none focus:shadow-md hover:border-slate-400 placeholder:text-slate-400"
      >
        {options.map(({ label, value: optionValue }) => (
          <option key={optionValue || label} value={optionValue}>
            {label}
          </option>
        ))}
      </select>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="1.2"
        stroke="currentColor"
        className="pointer-events-none absolute right-2.5 top-2.5 ml-1 h-5 w-5 text-slate-700"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
      </svg>
    </div>
  );

  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="flex flex-1 flex-wrap gap-4">
        <div className="min-w-[260px] flex-1">
          <label htmlFor="q" className="sr-only">
            Search recipes
          </label>
          <input
            id="q"
            name="q"
            type="search"
            value={localFilters.q ?? ""}
            onChange={handleTextChange}
            placeholder="Search recipes by name or ingredient"
            className="w-full rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 transition duration-300 ease focus:border-slate-400 focus:outline-none focus:shadow-md placeholder:text-slate-400"
          />
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-4">
        {user && (
          <div className="flex items-center gap-6">
            <div className="inline-flex items-center gap-2">
              <label htmlFor="created" className="cursor-pointer text-sm">
                My Recipes
              </label>
              <div className="relative inline-block h-5 w-11">
                <input
                  id="created"
                  name="created"
                  type="checkbox"
                  checked={localFilters.created === user.id}
                  onChange={handleCheckboxChange}
                  className="peer h-5 w-11 cursor-pointer appearance-none rounded-full bg-slate-200 transition-colors duration-300 checked:bg-slate-800"
                />
                <label
                  htmlFor="created"
                  className="absolute left-0 top-0 h-5 w-5 cursor-pointer rounded-full border border-slate-300 bg-white shadow-sm transition-transform duration-300 peer-checked:translate-x-6 peer-checked:border-slate-800"
                />
              </div>
            </div>
            <div className="inline-flex items-center gap-2">
              <label htmlFor="saved" className="cursor-pointer text-sm">
                Saved Recipes
              </label>
              <div className="relative inline-block h-5 w-11">
                <input
                  id="saved"
                  name="saved"
                  type="checkbox"
                  checked={localFilters.saved === user.id}
                  onChange={handleCheckboxChange}
                  className="peer h-5 w-11 cursor-pointer appearance-none rounded-full bg-slate-200 transition-colors duration-300 checked:bg-slate-800"
                />
                <label
                  htmlFor="saved"
                  className="absolute left-0 top-0 h-5 w-5 cursor-pointer rounded-full border border-slate-300 bg-white shadow-sm transition-transform duration-300 peer-checked:translate-x-6 peer-checked:border-slate-800"
                />
              </div>
            </div>
          </div>
        )}
        {renderSelect(
          "course",
          localFilters.course,
          [{ value: "", label: "All Courses" }, ...COURSES.map((course) => ({
            value: course.toLowerCase(),
            label: course,
          }))],
        )}
        {renderSelect(
          "cuisine",
          localFilters.cuisine,
          [{ value: "", label: "All Cuisines" }, ...CUISINES.map((cuisine) => ({
            value: cuisine.toLowerCase(),
            label: cuisine,
          }))],
        )}
        {renderSelect("sort", localFilters.sort, SORT_OPTIONS)}
      </div>
    </div>
  );
};

export default Filters;
